import { ref, onMounted, Ref } from 'vue';
import { useRoute, useRouter, RouteLocationNormalizedLoaded } from 'vue-router';
import { OfferService } from '@/api/services/offerService';
import { OfferStore } from '@/store/offerStore';
import { useSnackbar } from '@/composables/useSnackbar';
import { Offer } from '@/interfaces/Offers/Offer';

/**
 * Composable to handle opening offer dialog when offer ID is present in URL
 * Works with both route params (e.g., /offers/:offerId) and query params (e.g., ?offerId=123)
 */
export function useOfferFromUrl(
	dialogRef: Ref<boolean>
): { offerDetailsDialogOpen: Ref<boolean> } {
	const route = useRoute();
	const router = useRouter();
	const offerStore = OfferStore();

	// Function to handle opening offer dialog based on ID
	const openOfferDialog = async (offerId: string, currentRoute: RouteLocationNormalizedLoaded) => {
		try {
			// Show loading indicator
			const { showSnackbar } = useSnackbar();
			showSnackbar({
				message: 'Loading offer details...',
				color: 'info'
			});

			// Directly fetch the offer from API
			const offer = await OfferService.getOfferById(offerId);

			if (offer) {
				offerStore.selectOffer(offer);
				dialogRef.value = true;
			} else {
				throw new Error('Offer not found');
			}
		} catch (error) {
			console.error('Failed to fetch offer by ID:', error);
			// Show error message to the user
			const { showSnackbar } = useSnackbar();
			showSnackbar({
				message: 'The requested offer could not be found or is no longer available.',
				color: 'error'
			});

			// If we're on the offers route with a param, redirect to the main offers page
			if (currentRoute.name === 'OfferDetails') {
				router.push({ name: 'Offers' });
			}
		}
	};

	onMounted(async () => {
		// Check for offer ID in route params (e.g., /offers/:offerId)
		const paramOfferId = route.params.offerId;
		if (paramOfferId && typeof paramOfferId === 'string') {
			await openOfferDialog(paramOfferId, route);
			return;
		}

		// Check for offer ID in query params (e.g., ?offerId=123)
		const queryOfferId = route.query.offerId;
		if (queryOfferId && typeof queryOfferId === 'string') {
			await openOfferDialog(queryOfferId, route);
		}
	});

	return { offerDetailsDialogOpen: dialogRef };
}
